import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiStar, FiHeart, FiEye, FiSmile, FiTool } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiShield,
      title: 'General Dentistry',
      description: 'Comprehensive oral health care including cleanings, fillings, and preventive treatments.',
      features: ['Regular Cleanings', 'Dental Fillings', 'Root Canal Therapy', 'Tooth Extractions'],
      image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=500&h=300&fit=crop'
    },
    {
      icon: FiStar,
      title: 'Cosmetic Dentistry',
      description: 'Transform your smile with our advanced cosmetic dental procedures.',
      features: ['Teeth Whitening', 'Veneers', 'Dental Bonding', 'Smile Makeover'],
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=500&h=300&fit=crop'
    },
    {
      icon: FiTool,
      title: 'Orthodontics',
      description: 'Straighten your teeth and improve your bite with modern orthodontic solutions.',
      features: ['Traditional Braces', 'Clear Aligners', 'Retainers', 'Bite Correction'],
      image: 'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=500&h=300&fit=crop'
    },
    {
      icon: FiHeart,
      title: 'Pediatric Dentistry',
      description: 'Specialized dental care for children in a fun and comfortable environment.',
      features: ['Children\'s Cleanings', 'Fluoride Treatments', 'Sealants', 'Early Orthodontics'],
      image: 'https://images.unsplash.com/photo-1581594549595-35f6edc7b762?w=500&h=300&fit=crop'
    },
    {
      icon: FiSmile,
      title: 'Oral Surgery',
      description: 'Advanced surgical procedures performed with precision and care.',
      features: ['Wisdom Tooth Removal', 'Dental Implants', 'Bone Grafting', 'Gum Surgery'],
      image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=500&h=300&fit=crop'
    },
    {
      icon: FiEye,
      title: 'Emergency Care',
      description: '24/7 emergency dental services for urgent dental problems.',
      features: ['Tooth Pain Relief', 'Broken Tooth Repair', 'Lost Filling Replacement', 'Trauma Care'],
      image: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=500&h=300&fit=crop'
    }
  ];

  const procedures = [
    {
      title: 'Dental Implants',
      description: 'Permanent solution for missing teeth that look and feel natural.',
      duration: '1-3 months',
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=250&fit=crop'
    },
    {
      title: 'Teeth Whitening',
      description: 'Professional whitening treatment for a brighter, more confident smile.',
      duration: '1-2 hours',
      image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=400&h=250&fit=crop'
    },
    {
      title: 'Invisalign',
      description: 'Clear, removable aligners that straighten teeth without traditional braces.',
      duration: '12-18 months',
      image: 'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=400&h=250&fit=crop'
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Dental Services - DentalCare | Complete Oral Health Care</title>
        <meta name="description" content="Comprehensive dental services at DentalCare including general dentistry, cosmetic dentistry, orthodontics, pediatric care, oral surgery, and emergency services. Advanced technology and expert care." />
        <meta name="keywords" content="dental services, general dentistry, cosmetic dentistry, orthodontics, pediatric dentistry, oral surgery, emergency dental care, teeth whitening, dental implants" />
        <link rel="canonical" href="https://dentalcare.com/services" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
              We offer comprehensive dental services using the latest technology 
              and techniques to ensure optimal oral health and beautiful smiles.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={service.image}
                  alt={`${service.title} services at DentalCare`}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mr-4">
                      <SafeIcon icon={service.icon} className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-emerald-900">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Procedures */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Featured Procedures
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our most popular treatments that have helped thousands 
              of patients achieve their perfect smile.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {procedures.map((procedure, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <img
                  src={procedure.image}
                  alt={`${procedure.title} procedure at DentalCare`}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-emerald-900 mb-2">
                    {procedure.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{procedure.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-emerald-600 font-semibold">
                      Duration: {procedure.duration}
                    </span>
                    <button className="text-emerald-600 hover:text-emerald-700 font-semibold">
                      Learn More →
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-emerald-900 mb-6">
                Advanced Technology
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                We invest in the latest dental technology to provide more accurate 
                diagnoses, comfortable treatments, and better outcomes for our patients.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <span className="w-3 h-3 bg-emerald-600 rounded-full mr-4"></span>
                  <span className="text-gray-700">Digital X-rays and 3D Imaging</span>
                </li>
                <li className="flex items-center">
                  <span className="w-3 h-3 bg-emerald-600 rounded-full mr-4"></span>
                  <span className="text-gray-700">Laser Dentistry</span>
                </li>
                <li className="flex items-center">
                  <span className="w-3 h-3 bg-emerald-600 rounded-full mr-4"></span>
                  <span className="text-gray-700">Intraoral Cameras</span>
                </li>
                <li className="flex items-center">
                  <span className="w-3 h-3 bg-emerald-600 rounded-full mr-4"></span>
                  <span className="text-gray-700">Computer-Guided Surgery</span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=600&h=500&fit=crop"
                alt="Modern Dental Technology at DentalCare"
                className="rounded-lg shadow-lg w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-4">
              Schedule Your Consultation
            </h2>
            <p className="text-xl mb-8 text-emerald-100 max-w-2xl mx-auto">
              Ready to transform your smile? Contact us today to schedule a 
              consultation and learn more about our services.
            </p>
            <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors">
              Book Appointment
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;